import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { motion, AnimatePresence } from 'framer-motion';
import { Gift, Coffee, Gamepad2, Heart, Star, ShoppingBag, Book, Music, Smile, Globe } from 'lucide-react';
import type { Reward, RedeemedReward } from '../types/reward';
import { rewardService } from '../services/rewardService';
import { userService } from '../services/userService';
import { useNotification } from '../contexts/NotificationContext';
import { getFirestore, collection, query, where, orderBy, getDocs, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const RewardsContainer = styled.div`
  margin-top: 32px;
`;

const CreditsDisplay = styled.div`
  background: linear-gradient(135deg, #FFD700 0%, #FFA500 100%);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 32px;
  text-align: center;
  box-shadow: 0 8px 24px rgba(255, 215, 0, 0.2);
  border: 1px solid #FFE135;
`;

const CreditsTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #8B4513;
  margin: 0 0 8px 0;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CreditsAmount = styled.div`
  font-size: 48px;
  font-weight: 900;
  color: #8B4513;
  margin-bottom: 8px;
`;

const CreditsSubtitle = styled.p`
  font-size: 14px;
  color: #8B4513;
  opacity: 0.8;
  margin: 0;
`;

const RewardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const RewardCard = styled(motion.div)<{ isAvailable: boolean }>`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid ${props => props.isAvailable ? '#e2e8f0' : '#f1f5f9'};
  transition: all 0.2s;
  opacity: ${props => props.isAvailable ? 1 : 0.6};
  position: relative;

  &:hover {
    ${props => props.isAvailable && `
      transform: translateY(-4px);
      box-shadow: 0 8px 30px rgba(0, 0, 0, 0.12);
      border-color: #F57C00;
    `}
  }
`;

const RewardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 16px;
`;

const RewardIcon = styled.div<{ category: string }>`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => {
    switch (props.category) {
      case 'break': return 'linear-gradient(135deg, #10B981 0%, #059669 100%)';
      case 'treat': return 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)';
      case 'experience': return 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)';
      case 'gift': return 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)';
      default: return 'linear-gradient(135deg, #6B7280 0%, #4B5563 100%)';
    }
  }};
  color: white;
  font-size: 20px;
`;

const RewardInfo = styled.div`
  flex: 1;
`;

const RewardName = styled.h3`
  font-size: 20px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

const RewardDescription = styled.p`
  font-size: 14px;
  color: #64748b;
  margin: 0;
  line-height: 1.5;
`;

const RewardCost = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: 700;
  color: #8B4513;
  margin-top: 16px;
`;

const PurchaseButton = styled(motion.button)<{ canAfford: boolean }>`
  background: ${props => props.canAfford 
    ? 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)' 
    : '#94a3b8'};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.canAfford ? 'pointer' : 'not-allowed'};
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    ${props => props.canAfford && `
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(245, 124, 0, 0.3);
    `}
  }
`;

const PurchasedRewards = styled.div`
  margin-top: 48px;
`;

const PurchasedTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: #1e293b;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #64748b;
`;

const EmptyStateIcon = styled.div`
  font-size: 64px;
  margin-bottom: 24px;
  opacity: 0.5;
`;

const EmptyStateTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
`;

const EmptyStateText = styled.p`
  font-size: 16px;
  margin: 0;
`;

interface RewardsProps {
  userId: string;
  availableCredits: number;
  onPurchaseReward: (rewardId: string, cost: number) => void;
}

const Rewards: React.FC<RewardsProps> = ({ userId, availableCredits, onPurchaseReward }) => {
  const [rewards, setRewards] = useState<Reward[]>([]);
  const [purchasedRewards, setPurchasedRewards] = useState<RedeemedReward[]>([]);
  const [canProposeReward, setCanProposeReward] = useState(false);
  const [showProposalForm, setShowProposalForm] = useState(false);
  const [proposal, setProposal] = useState({
    name: '',
    description: '',
    category: '',
    imageUrl: ''
  });
  const [submittingProposal, setSubmittingProposal] = useState(false);
  const [loadingRewards, setLoadingRewards] = useState(true);
  const [myProposals, setMyProposals] = useState<Reward[]>([]);
  const [loadingProposals, setLoadingProposals] = useState(true);
  const [editingProposal, setEditingProposal] = useState<Reward | null>(null);
  const [editForm, setEditForm] = useState({ name: '', description: '', category: '' });
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    // Fetch approved rewards from Firestore
    const fetchRewards = async () => {
      setLoadingRewards(true);
      try {
        const approved = await rewardService.getApprovedRewards();
        setRewards(approved);
      } catch (err) {
        setRewards([]);
      } finally {
        setLoadingRewards(false);
      }
    };
    fetchRewards();
    userService.hasAcceptedCreditApprover().then(setCanProposeReward).catch(() => setCanProposeReward(false));
    // Fetch purchased rewards from Firestore
    const fetchPurchased = async () => {
      try {
        const purchased = await rewardService.getMyRedeemedRewards();
        setPurchasedRewards(purchased);
      } catch (err) {
        setPurchasedRewards([]);
      }
    };
    fetchPurchased();
  }, []);

  // Listen for proposal notifications for this user
  useEffect(() => {
    const fetchNotifications = async () => {
      const db = getFirestore();
      const q = query(
        collection(db, 'notifications'),
        where('userId', '==', userId),
        where('read', '==', false),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        if (data.type === 'approved') {
          showSuccess(`Your reward proposal "${data.rewardName}" was approved!`);
        } else if (data.type === 'rejected') {
          showError(`Your reward proposal "${data.rewardName}" was rejected.`);
        }
        // Mark as read
        await updateDoc(doc(db, 'notifications', docSnap.id), { read: true });
      }
    };
    if (userId) {
      fetchNotifications();
    }
  }, [userId]);

  // Fetch my proposals
  useEffect(() => {
    const fetchProposals = async () => {
      setLoadingProposals(true);
      try {
        const proposals = await rewardService.getUserRewardProposals();
        setMyProposals(proposals);
      } catch (error) {
        console.error('Error fetching proposals:', error);
        setMyProposals([]);
      } finally {
        setLoadingProposals(false);
      }
    };
    if (userId) fetchProposals();
  }, [userId, submittingProposal, editingProposal]);



  // Edit proposal handlers
  const startEditProposal = (proposal: Reward) => {
    setEditingProposal(proposal);
    setEditForm({ name: proposal.name, description: proposal.description, category: proposal.category });
  };
  const handleEditFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => ({ ...prev, [name]: value }));
  };
  const handleEditCategory = (cat: string) => {
    setEditForm(prev => ({ ...prev, category: cat }));
  };
  const handleResubmitProposal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingProposal) return;
    if (!editForm.name.trim() || !editForm.description.trim() || !editForm.category.trim()) {
      showError('Please fill in all required fields.');
      return;
    }
    try {
      const db = getFirestore();
      await updateDoc(doc(db, 'rewards', editingProposal.id), {
        name: editForm.name,
        description: editForm.description,
        category: editForm.category,
        status: 'pending_approval',
        updatedAt: new Date(),
      });
      showSuccess('Proposal resubmitted for approval!');
      setEditingProposal(null);
    } catch (err) {
      showError('Failed to resubmit proposal.');
    }
  };

  const handleDeleteProposal = async (proposalId: string) => {
    if (!window.confirm('Are you sure you want to delete this proposal? This action cannot be undone.')) {
      return;
    }
    try {
      const db = getFirestore();
      await deleteDoc(doc(db, 'rewards', proposalId));
      showSuccess('Proposal deleted successfully!');
      // Refresh the proposals list
      setMyProposals(prev => prev.filter(p => p.id !== proposalId));
    } catch (err) {
      showError('Failed to delete proposal.');
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'break': return <Coffee />;
      case 'treat': return <Heart />;
      case 'experience': return <Gamepad2 />;
      case 'gift': return <Gift />;
      default: return <Star />;
    }
  };

  const handlePurchase = async (reward: Reward) => {
    const cost = typeof reward.cost === 'number' ? reward.cost : 0;
    if (availableCredits >= cost) {
      try {
        // Redeem the reward using the service
        await rewardService.redeemReward(
          reward.id,
          reward.name,
          reward.description,
          cost,
          reward.category
        );
        // Call the parent handler
        onPurchaseReward(reward.id, cost);
        // Add to purchased rewards
        showSuccess(`Successfully redeemed ${reward.name}!`);
      } catch (error) {
        console.error('Error redeeming reward:', error);
        const errorMessage = error instanceof Error ? error.message : 'Failed to redeem reward';
        showError(errorMessage);
      }
    }
  };

  const handleUseReward = async (userReward: RedeemedReward) => {
    try {
      // Request approval from credit approvers instead of directly using
      await rewardService.requestRewardUsageApproval(userReward.id);
      showSuccess('Reward usage request sent to your credit approvers. You will be notified when confirmed.');
      
      // Update local state to show pending status
      setPurchasedRewards(prev => 
        prev.map(reward => 
          reward.id === userReward.id 
            ? { ...reward, status: 'pending_approval', approvalRequestedAt: new Date() }
            : reward
        )
      );
    } catch (error) {
      console.error('Error requesting reward usage approval:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to request approval';
      showError(errorMessage);
    }
  };

  // Proposal form handlers
  const handleProposalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setProposal(prev => ({ ...prev, [name]: value }));
  };

  const handleProposalSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!proposal.name.trim() || !proposal.description.trim() || !proposal.category.trim()) {
      showError('Please fill in all required fields.');
      return;
    }
    setSubmittingProposal(true);
    try {
      await rewardService.proposeReward(proposal.name, proposal.description, proposal.category, proposal.imageUrl);
      showSuccess('Reward proposal submitted for approval! You can track its status in the "My Proposals" section below.');
      setShowProposalForm(false);
      setProposal({ name: '', description: '', category: '', imageUrl: '' });
    } catch (err) {
      showError((err instanceof Error ? err.message : 'Failed to submit proposal'));
    } finally {
      setSubmittingProposal(false);
    }
  };

  // Helper for icon sizing
  const iconStyle = { width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' };
  const iconButtonStyle = {
    width: 64,
    height: 64,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 12,
    padding: 0,
    margin: 0,
    background: '#fff',
    border: '1.5px solid #e2e8f0',
    transition: 'all 0.2s',
    outline: 'none',
    cursor: 'pointer',
    boxShadow: 'none',
  };

  return (
    <RewardsContainer>
      <CreditsDisplay>
        <CreditsTitle>Available Credits</CreditsTitle>
        <CreditsAmount>{availableCredits}</CreditsAmount>
        <CreditsSubtitle>Earn more credits by completing missions!</CreditsSubtitle>
      </CreditsDisplay>

      {loadingRewards ? (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748b' }}>Loading rewards...</div>
      ) : (
        <>
          <h2>Available Rewards</h2>
          <RewardsGrid>
            <AnimatePresence>
              {rewards.map((reward) => (
                <RewardCard
                  key={reward.id}
                  isAvailable={typeof reward.available !== 'undefined' ? reward.available : false}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  <RewardHeader>
                    <RewardIcon category={reward.category}>
                      {getCategoryIcon(reward.category)}
                    </RewardIcon>
                    <RewardInfo>
                      <RewardName>{reward.name}</RewardName>
                      <RewardDescription>{reward.description}</RewardDescription>
                    </RewardInfo>
                  </RewardHeader>
                  <RewardCost>
                    <Star size={16} />
                    {typeof reward.cost === 'number' ? reward.cost : 0} Credits
                  </RewardCost>
                  <PurchaseButton
                    canAfford={availableCredits >= (typeof reward.cost === 'number' ? reward.cost : 0)}
                    onClick={() => handlePurchase({ ...reward, cost: typeof reward.cost === 'number' ? reward.cost : 0 })}
                    whileHover={{ scale: availableCredits >= (typeof reward.cost === 'number' ? reward.cost : 0) ? 1.02 : 1 }}
                    whileTap={{ scale: availableCredits >= (typeof reward.cost === 'number' ? reward.cost : 0) ? 0.98 : 1 }}
                  >
                    <ShoppingBag size={16} />
                    {availableCredits >= (typeof reward.cost === 'number' ? reward.cost : 0) ? 'Purchase' : 'Not Enough Credits'}
                  </PurchaseButton>
                </RewardCard>
              ))}
            </AnimatePresence>
          </RewardsGrid>
        </>
      )}



      {canProposeReward && (
        <div style={{ marginBottom: 32, textAlign: 'right' }}>
          <button
            style={{
              background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 24px',
              fontWeight: 700,
              fontSize: 16,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(245, 124, 0, 0.12)',
              transition: 'all 0.2s'
            }}
            onClick={() => setShowProposalForm(v => !v)}
          >
            {showProposalForm ? 'Cancel' : 'Propose Reward'}
          </button>
        </div>
      )}
      {showProposalForm && (
        <form onSubmit={handleProposalSubmit} style={{ marginBottom: 32, background: '#f8fafc', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', maxWidth: 500, marginLeft: 'auto', marginRight: 'auto' }}>
          <h3 style={{ marginTop: 0, color: '#F57C00', fontWeight: 800 }}>Propose a New Reward</h3>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600 }}>Name *</label>
            <input
              type="text"
              name="name"
              value={proposal.name}
              onChange={handleProposalChange}
              required
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1.5px solid #FFE135', marginTop: 4, fontSize: 16 }}
            />
          </div>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontWeight: 600 }}>Description *</label>
            <textarea
              name="description"
              value={proposal.description}
              onChange={handleProposalChange}
              required
              rows={3}
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1.5px solid #FFE135', marginTop: 4, fontSize: 16 }}
            />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Category *</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
              {[
                { key: 'break', icon: <span style={iconStyle}><Coffee size={28} /></span>, label: 'Break' },
                { key: 'treat', icon: <span style={iconStyle}><Heart size={28} /></span>, label: 'Treat' },
                { key: 'experience', icon: <span style={iconStyle}><Gamepad2 size={28} /></span>, label: 'Experience' },
                { key: 'gift', icon: <span style={iconStyle}><Gift size={28} /></span>, label: 'Gift' },
                { key: 'star', icon: <span style={iconStyle}><Star size={28} /></span>, label: 'Star' },
                { key: 'shopping', icon: <span style={iconStyle}><ShoppingBag size={28} /></span>, label: 'Shopping' },
                { key: 'book', icon: <span style={iconStyle}><Book size={28} /></span>, label: 'Book' },
                { key: 'music', icon: <span style={iconStyle}><Music size={28} /></span>, label: 'Music' },
                { key: 'smile', icon: <span style={iconStyle}><Smile size={28} /></span>, label: 'Smile' },
                { key: 'globe', icon: <span style={iconStyle}><Globe size={28} /></span>, label: 'Globe' }
              ].map(cat => (
                <button
                  key={cat.key}
                  type="button"
                  onClick={() => setProposal(prev => ({ ...prev, category: cat.key }))}
                  style={{
                    ...iconButtonStyle,
                    background: proposal.category === cat.key ? 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)' : '#fff',
                    border: proposal.category === cat.key ? '2.5px solid #F57C00' : '1.5px solid #e2e8f0',
                    boxShadow: proposal.category === cat.key ? '0 4px 16px rgba(245, 124, 0, 0.10)' : 'none',
                  } as React.CSSProperties}
                  aria-label={cat.label}
                >
                  {cat.icon}
                </button>
              ))}
            </div>
          </div>
          <button
            type="submit"
            disabled={submittingProposal}
            style={{
              background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)',
              color: 'white',
              border: 'none',
              borderRadius: 8,
              padding: '10px 24px',
              fontWeight: 700,
              fontSize: 16,
              cursor: submittingProposal ? 'not-allowed' : 'pointer',
              boxShadow: '0 2px 8px rgba(245, 124, 0, 0.12)',
              transition: 'all 0.2s'
            }}
          >
            {submittingProposal ? 'Submitting...' : 'Submit Proposal'}
          </button>
        </form>
      )}

      {(() => {
        const pendingProposals = myProposals.filter(p => p.status === 'pending_approval');
        if (pendingProposals.length === 0) return null;
        return (
          <div style={{ margin: '48px 0 32px 0' }}>
            <h2 style={{ color: '#F57C00', fontWeight: 800, marginBottom: 16 }}>My Proposals</h2>
            <div style={{ display: 'grid', gap: 20 }}>
              {pendingProposals.map(proposal => (
                <div key={proposal.id} style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', padding: 20, border: '1.5px solid #FFE135' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 24 }}>
                    <RewardIcon category={proposal.category}>
                      {getCategoryIcon(proposal.category)}
                    </RewardIcon>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>{proposal.name}</div>
                        <span style={{ 
                          fontSize: 12, 
                          padding: '4px 8px', 
                          borderRadius: 12, 
                          fontWeight: 600,
                          background: '#fef3c7',
                          color: '#d97706'
                        }}>
                          PENDING APPROVAL
                        </span>
                      </div>
                      <div style={{ color: '#64748b', marginBottom: 8 }}>{proposal.description}</div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                        <span style={{ fontWeight: 600, color: '#F57C00', textTransform: 'capitalize' }}>{proposal.category}</span>
                        <span style={{ fontSize: 12, color: '#64748b' }}>
                          Created: {proposal.createdAt?.toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })()}

      {/* Edit Proposal Modal */}
      {editingProposal && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <form onSubmit={handleResubmitProposal} style={{ background: '#fff', borderRadius: 16, padding: 32, minWidth: 340, maxWidth: 400, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' }}>
            <h3 style={{ marginTop: 0, color: '#F57C00', fontWeight: 800 }}>Edit & Resubmit Proposal</h3>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600 }}>Name *</label>
              <input
                type="text"
                name="name"
                value={editForm.name}
                onChange={handleEditFormChange}
                required
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1.5px solid #FFE135', marginTop: 4, fontSize: 16 }}
              />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontWeight: 600 }}>Description *</label>
              <textarea
                name="description"
                value={editForm.description}
                onChange={handleEditFormChange}
                required
                rows={3}
                style={{ width: '100%', padding: 10, borderRadius: 8, border: '1.5px solid #FFE135', marginTop: 4, fontSize: 16 }}
              />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>Category *</label>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16 }}>
                {[
                  { key: 'break', icon: <span style={iconStyle}><Coffee size={28} /></span> },
                  { key: 'treat', icon: <span style={iconStyle}><Heart size={28} /></span> },
                  { key: 'experience', icon: <span style={iconStyle}><Gamepad2 size={28} /></span> },
                  { key: 'gift', icon: <span style={iconStyle}><Gift size={28} /></span> },
                  { key: 'star', icon: <span style={iconStyle}><Star size={28} /></span> },
                  { key: 'shopping', icon: <span style={iconStyle}><ShoppingBag size={28} /></span> },
                  { key: 'book', icon: <span style={iconStyle}><Book size={28} /></span> },
                  { key: 'music', icon: <span style={iconStyle}><Music size={28} /></span> },
                  { key: 'smile', icon: <span style={iconStyle}><Smile size={28} /></span> },
                  { key: 'globe', icon: <span style={iconStyle}><Globe size={28} /></span> }
                ].map(cat => (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => handleEditCategory(cat.key)}
                    style={{
                      ...iconButtonStyle,
                      background: editForm.category === cat.key ? 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)' : '#fff',
                      border: editForm.category === cat.key ? '2.5px solid #F57C00' : '1.5px solid #e2e8f0',
                      boxShadow: editForm.category === cat.key ? '0 4px 16px rgba(245, 124, 0, 0.10)' : 'none',
                    } as React.CSSProperties}
                    aria-label={cat.key}
                  >
                    {cat.icon}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" onClick={() => setEditingProposal(null)} style={{ background: '#e2e8f0', color: '#374151', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, fontSize: 15, cursor: 'pointer' }}>Cancel</button>
              <button type="submit" style={{ background: 'linear-gradient(135deg, #F57C00 0%, #E65100 100%)', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 700, fontSize: 15, cursor: 'pointer' }}>Resubmit</button>
            </div>
          </form>
        </div>
      )}

      {rewards.length === 0 && (
        <EmptyState>
          <EmptyStateIcon>🎁</EmptyStateIcon>
          <EmptyStateTitle>No Rewards Available</EmptyStateTitle>
          <EmptyStateText>Complete more missions to unlock rewards!</EmptyStateText>
        </EmptyState>
      )}
    </RewardsContainer>
  );
};

export default Rewards;